import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom'
import { useEffect } from 'react'
import i18n from './i18n/i18n'

import Home from './Home'
import AuthModal from './components/shared/AuthModal'
import Step5 from './components/steps/Step5'
import QuizFlow from './components/quiz/QuizFlow'
import { STORAGE_KEYS } from './constants'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { growthbook } from './lib/growthbookClient'
import useGTM from './hooks/useGtm'
import { GrowthBookProvider as CustomGrowthBookProvider } from './contexts/GrowthBookContext'
import { VariantProvider } from './contexts/VariantContext'
import { VARIANT_SLUGS, getVariantBySlug } from './config/landingVariants'
import { ConsentProvider } from './contexts/ConsentContext'
import CookieBanner from './components/CookieBanner'
import CookieManager from './components/CookieManager'

const supported = ['en', 'fr', 'de', 'es', 'it', 'ja', 'ar', 'id', 'ko', 'nl', 'pl', 'pt', 'ru', 'tr', 'zh']

const browserLang = (() => {
  const lang = navigator.language.split('-')[0]
  return supported.includes(lang) ? lang : 'en'
})()

function LanguageWrapper() {
  useGTM()

  const { lang } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!lang) {
      i18n.changeLanguage(browserLang)
      return
    }

    if (supported.includes(lang)) {
      i18n.changeLanguage(lang)
      if (lang === browserLang && location.pathname.startsWith(`/${browserLang}`)) {
        const cleanPath = location.pathname.replace(new RegExp(`^/${browserLang}`), '') || '/'
        navigate(cleanPath, { replace: true })
      }
      if (lang === 'en' && browserLang === 'en' && location.pathname.startsWith('/en')) {
        const cleanPath = location.pathname.replace(/^\/en/, '') || '/'
        navigate(cleanPath, { replace: true })
      }
    } else {
      navigate('/', { replace: true })
    }
  }, [lang, location.pathname, navigate])

  return (
    <>
      <Outlet />
    </>
  )
}

function useQuizStyling() {
  useEffect(() => {
    document.body.classList.add('ndg-mode')
    document.body.classList.add('quiz-mode')
    return () => {
      document.body.classList.remove('ndg-mode')
      document.body.classList.remove('quiz-mode')
    }
  }, [])
}

function SummaryPage() {
  useQuizStyling()
  return <Step5 />
}

function SignupModalPage() {
  useQuizStyling()
  const navigate = useNavigate()
  const style = (() => {
    try {
      const sel = JSON.parse(localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}')
      return sel.welcome || 'Realistic'
    } catch {
      return 'Realistic'
    }
  })()
  return (
    <AuthModal
      isOpen={true}
      onClose={() => navigate(-1)}
      selectedStyle={style}
    />
  )
}

function VariantLanguageWrapper({ slug }) {
  useGTM()

  const { lang } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const variant = getVariantBySlug(slug)

  useEffect(() => {
    // No per-variant document.title: the tab title is one site-wide string set
    // in public/index.html. Variants still change the on-page H1 via titleParts.

    if (!lang) {
      i18n.changeLanguage(browserLang)
      return
    }

    if (supported.includes(lang)) {
      i18n.changeLanguage(lang)
      if (lang === browserLang && location.pathname.startsWith(`/${browserLang}/`)) {
        const cleanPath = location.pathname.replace(new RegExp(`^/${browserLang}`), '') || '/'
        navigate(cleanPath, { replace: true })
      }
      if (lang === 'en' && browserLang === 'en' && location.pathname.startsWith('/en/')) {
        const cleanPath = location.pathname.replace(/^\/en/, '') || '/'
        navigate(cleanPath, { replace: true })
      }
    } else {
      navigate('/', { replace: true })
    }
  }, [variant, lang, location.pathname, navigate])

  return (
    <VariantProvider variant={variant}>
      <Outlet />
    </VariantProvider>
  )
}

export default function Root() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const canRedirect = urlParams.get('redirect') !== 'false'

    const inProgress = localStorage.getItem('googleAuthInProgress')
    const startTime = parseInt(localStorage.getItem('googleAuthStartTime'), 10)

    if (inProgress && startTime) {
      const timeElapsed = Date.now() - startTime

      if (timeElapsed < 3 * 60 * 1000) {
        localStorage.setItem('googleAuthCancelled', 'true')
      } else {
        localStorage.setItem('isCompleted', true)
        localStorage.removeItem('googleAuthCancelled')
      }

      localStorage.removeItem('googleAuthInProgress')
      localStorage.removeItem('googleAuthStartTime')
    }

    // paywall_popup Google return lands here with ?auth_token — stay on the lander
    // (createAccount reopens the popup) instead of redirecting to /paywall.
    const isPaywallPopupReturn = urlParams.has('auth_token')

    const isCompleted = localStorage.getItem('isCompleted')
    if (isCompleted && canRedirect && !isPaywallPopupReturn) {
      const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL
      const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`)
      paywallUrl.searchParams.set('from_landing', 'true')
      // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
      paywallUrl.searchParams.set('protection', 'sfw')
      window.location.href = paywallUrl.toString()
    }
  }, [])


  return (
    <ConsentProvider>
      <GrowthBookProvider growthbook={growthbook}>
        <CustomGrowthBookProvider>
          <Router>
            <Routes>
            <Route path="/" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignupModalPage />} />
              <Route path="summary" element={<SummaryPage />} />
              <Route path="newaccount" element={<Home />} />
              <Route path="pw" element={<Home />} />
            </Route>

            <Route path="/ra" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignupModalPage />} />
              <Route path="summary" element={<SummaryPage />} />
              <Route path="newaccount" element={<Home />} />
              <Route path="pw" element={<Home />} />
            </Route>

            <Route path="/halloween" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignupModalPage />} />
              <Route path="summary" element={<SummaryPage />} />
            </Route>

            <Route path="/quiz" element={<LanguageWrapper />}>
              <Route index element={<QuizFlow />} />
            </Route>

            <Route path="/quiz-sfw" element={<LanguageWrapper />}>
              <Route index element={<QuizFlow />} />
            </Route>

            <Route path="/:lang" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignupModalPage />} />
              <Route path="summary" element={<SummaryPage />} />
              <Route path="newaccount" element={<Home />} />
              <Route path="pw" element={<Home />} />
            </Route>

            <Route path="/:lang/halloween" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignupModalPage />} />
              <Route path="summary" element={<SummaryPage />} />
            </Route>

            <Route path="/:lang/ra" element={<LanguageWrapper />}>
              <Route index element={<Home />} />
              <Route path="signup" element={<SignupModalPage />} />
              <Route path="summary" element={<SummaryPage />} />
              <Route path="newaccount" element={<Home />} />
              <Route path="pw" element={<Home />} />
            </Route>

            <Route path="/:lang/quiz" element={<LanguageWrapper />}>
              <Route index element={<QuizFlow />} />
            </Route>

            <Route path="/:lang/quiz-sfw" element={<LanguageWrapper />}>
              <Route index element={<QuizFlow />} />
            </Route>

            {VARIANT_SLUGS.map(slug => (
              <Route key={slug} path={`/${slug}`} element={<VariantLanguageWrapper slug={slug} />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>
            ))}

            {VARIANT_SLUGS.map(slug => (
              <Route key={`lang-${slug}`} path={`/:lang/${slug}`} element={<VariantLanguageWrapper slug={slug} />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>
            ))}

            <Route path="/cookie-manager" element={<CookieManager />} />

            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieBanner />
          </Router>
        </CustomGrowthBookProvider>
      </GrowthBookProvider>
    </ConsentProvider>
  )
}
